import { useCallback, useEffect, useMemo, useState } from "react";

import { DeliveryType } from "../../../common/types/enums";
import {
  CartItem,
  DeliveryRule,
  OrderInterface,
  PromoInterface,
  Rule,
} from "../../../common/types/interfaces";
import { getApi } from "../../../api/api";

interface UseCartValidationReturn {
  applicablePromos: PromoInterface[];
  clusterDeliveryRules: DeliveryRule[];
  calculateDiscount: (
    itemsTotal: number,
    promo?: PromoInterface,
    cartItems?: CartItem[],
    storeId?: number,
    discountCode?: string,
    deliveryType?: DeliveryType,
  ) => number;
  findApplicablePromos: (
    promos: PromoInterface[],
    cartItems: CartItem[],
    storeId?: number,
    deliveryType?: DeliveryType,
  ) => PromoInterface[];
  isPromoApplicable: (
    promo: PromoInterface,
    cartItems: CartItem[],
    storeId?: number,
    discountCode?: string,
    deliveryType?: DeliveryType,
  ) => boolean;
  getBestDeliveryRuleText: (
    itemsTotal: number,
    applicableRules?: DeliveryRule[],
  ) => string | null;
  roundMoney: (amount: number) => number;
  calculateItemsTotal: (cartItems: CartItem[]) => number;
  calculateMaxWriteOff: (
    orderTotal: number,
    userBonuses: number,
    writeOffPercent: number,
  ) => number;
  calculateEarnedBonuses: (
    itemsTotal: number,
    bonusPercent: number,
    bonusesToSpend: number,
  ) => number;
  getNextBonusLevelText: (
    userTotalSpent: number,
    bonusRules: Rule[],
  ) => string | null;
}

export const useCartValidation = (): UseCartValidationReturn => {
  const api = getApi();

  const [orders, setOrders] = useState<OrderInterface[]>([]);

  useEffect(() => {
    let cancelled = false;

    api
      .getOrders({ limit: 100 })
      .then((list) => {
        if (!cancelled) setOrders(list || []);
      })
      .catch(() => {
        if (!cancelled) setOrders([]);
      });

    return () => {
      cancelled = true;
    };
  }, [api]);

  const purchased = useMemo(() => {
    const itemIds = new Set<string>();
    const categoryIds = new Set<number>();
    const storeIds = new Set<number>();
    let hasNonCancelledOrders = false;

    orders.forEach((order) => {
      if (order.status !== undefined) hasNonCancelledOrders = true;
      (order.items || []).forEach((it) => {
        itemIds.add(String(it.itemId));
      });
      if (order.storeId) storeIds.add(order.storeId);
    });

    return { itemIds, categoryIds, storeIds, hasNonCancelledOrders };
  }, [orders]);

  const clusterDeliveryRules = useMemo(() => [] as DeliveryRule[], []);

  const roundMoney = (amount: number): number => {
    return Math.round(amount * 100) / 100;
  };

  const calculateItemsTotal = useCallback((cartItems: CartItem[]): number => {
    let total = 0;
    for (let i = 0; i < cartItems.length; i++) {
      const item = cartItems[i];
      total += item.price * item.quantity;
    }
    return roundMoney(total);
  }, []);

  const isPromoDateValid = useCallback((promo: PromoInterface): boolean => {
    if (!promo.datesImportant) return true;
    const now = new Date();
    const startDate = new Date(promo.startDate);
    const endDate = new Date(promo.endDate);
    return startDate <= now && endDate >= now;
  }, []);

  const findApplicablePromos = useCallback(
    (
      promos: PromoInterface[],
      cartItems: CartItem[],
      storeId?: number,
      deliveryType?: DeliveryType,
    ): PromoInterface[] => {
      if (!promos.length || !cartItems.length) return [];

      const itemIds = new Set(cartItems.map((item) => String(item.saleItemId)));
      const categoryIds = new Set(
        cartItems.map((item) => item.categoryId).filter(Boolean),
      );
      const itemsTotal = calculateItemsTotal(cartItems);

      return promos.filter((promo) => {
        if (!promo.isActive) return false;

        if (!promo.giftBySum) {
          const discountPercent = Number(promo.discount);
          if (isNaN(discountPercent) || discountPercent <= 0) return false;
        }

        if (!isPromoDateValid(promo)) return false;

        if (promo.forPickup) {
          if (deliveryType !== DeliveryType.PICKUP) return false;
          if (promo.minSum && itemsTotal < promo.minSum) return false;
        }

        if (promo.giftBySum) {
          if (itemsTotal < (promo.minSum || 0)) return false;
          return true;
        }

        if (promo.forFirstPurchase) {
          if (promo.useItems && promo.itemsFor?.length) {
            const hasPurchasedItem = promo.itemsFor.some((id) =>
              purchased.itemIds.has(String(id)),
            );
            if (hasPurchasedItem) return false;
          } else if (promo.useCategories && promo.categoriesFor?.length) {
            const hasPurchasedCategory = promo.categoriesFor.some((id) =>
              purchased.categoryIds.has(id),
            );
            if (hasPurchasedCategory) return false;
          } else if (promo.useStores && promo.storesFor?.length) {
            const hasPurchasedStore = promo.storesFor.some((id) =>
              purchased.storeIds.has(id),
            );
            if (hasPurchasedStore) return false;
          } else if (purchased.hasNonCancelledOrders) {
            return false;
          }
        }

        if (promo.useStores && storeId && promo.storesFor?.length) {
          if (!promo.storesFor.includes(storeId)) return false;
        }

        if (promo.useItems && promo.itemsFor?.length) {
          const hasMatchingItem = promo.itemsFor.some((id) =>
            itemIds.has(String(id)),
          );
          if (!hasMatchingItem) return false;
        } else if (promo.useCategories && promo.categoriesFor?.length) {
          const hasMatchingCategory = promo.categoriesFor.some((catId) =>
            categoryIds.has(catId),
          );
          if (!hasMatchingCategory) return false;
        }

        return true;
      });
    },
    [isPromoDateValid, calculateItemsTotal, purchased],
  );

  const isPromoApplicable = useCallback(
    (
      promo: PromoInterface,
      cartItems: CartItem[],
      storeId?: number,
      discountCode?: string,
      deliveryType?: DeliveryType,
    ): boolean => {
      if (!promo.isActive) return false;

      if (!promo.giftBySum) {
        const discountPercent = Number(promo.discount);
        if (isNaN(discountPercent) || discountPercent <= 0) return false;
      }

      if (!isPromoDateValid(promo)) return false;

      if (promo.forPickup) {
        if (deliveryType !== DeliveryType.PICKUP) return false;
        if (promo.minSum) {
          const itemsTotal = calculateItemsTotal(cartItems);
          if (itemsTotal < promo.minSum) return false;
        }
      }

      if (promo.giftBySum) {
        const itemsTotal = calculateItemsTotal(cartItems);
        if (itemsTotal < (promo.minSum || 0)) return false;
      }

      if (promo.forFirstPurchase) {
        if (promo.useItems && promo.itemsFor?.length) {
          const hasPurchasedItem = promo.itemsFor.some((id) =>
            purchased.itemIds.has(String(id)),
          );
          if (hasPurchasedItem) return false;
        } else if (promo.useCategories && promo.categoriesFor?.length) {
          const hasPurchasedCategory = promo.categoriesFor.some((id) =>
            purchased.categoryIds.has(id),
          );
          if (hasPurchasedCategory) return false;
        } else if (promo.useStores && promo.storesFor?.length) {
          const hasPurchasedStore = promo.storesFor.some((id) =>
            purchased.storeIds.has(id),
          );
          if (hasPurchasedStore) return false;
        } else if (purchased.hasNonCancelledOrders) {
          return false;
        }
      }

      if (promo.useDiscountCode) {
        const enteredCode = discountCode?.trim().toLowerCase();
        const promoCode = promo.discountCode?.trim().toLowerCase();
        if (!enteredCode || enteredCode !== promoCode) return false;
      }

      if (promo.useStores && storeId && promo.storesFor?.length) {
        if (!promo.storesFor.includes(storeId)) return false;
      }

      const itemIds = new Set(cartItems.map((item) => String(item.saleItemId)));
      const categoryIds = new Set(
        cartItems.map((item) => item.categoryId).filter(Boolean),
      );

      if (promo.useItems && promo.itemsFor?.length) {
        const hasMatchingItem = promo.itemsFor.some((id) =>
          itemIds.has(String(id)),
        );
        if (!hasMatchingItem) return false;
      } else if (promo.useCategories && promo.categoriesFor?.length) {
        const hasMatchingCategory = promo.categoriesFor.some((id) =>
          categoryIds.has(id),
        );
        if (!hasMatchingCategory) return false;
      }

      const itemsTotal = calculateItemsTotal(cartItems);
      if (itemsTotal <= 0) return false;

      return true;
    },
    [isPromoDateValid, calculateItemsTotal, purchased],
  );

  const calculateDiscount = useCallback(
    (
      itemsTotal: number,
      promo?: PromoInterface,
      cartItems?: CartItem[],
      storeId?: number,
      discountCode?: string,
      deliveryType?: DeliveryType,
    ): number => {
      if (!promo || !cartItems || cartItems.length === 0) return 0;
      if (promo.giftBySum) return 0;

      if (
        !isPromoApplicable(
          promo,
          cartItems,
          storeId,
          discountCode,
          deliveryType,
        )
      ) {
        return 0;
      }

      const discountPercent = Number(promo.discount);
      const rawDiscount = itemsTotal * (discountPercent / 100);
      const discount = Math.ceil(rawDiscount);
      return discount < itemsTotal ? discount : itemsTotal;
    },
    [isPromoApplicable],
  );

  const getBestDeliveryRuleText = useCallback(
    (itemsTotal: number, applicableRules?: DeliveryRule[]): string | null => {
      const rules = applicableRules?.length
        ? applicableRules
        : clusterDeliveryRules;
      if (rules.length < 2) return null;

      const sorted = [...rules].sort(
        (a, b) => Number(a.level) - Number(b.level),
      );
      const futureRules = sorted.filter((r) => Number(r.level) > itemsTotal);
      if (!futureRules.length) return null;

      const nextRule = futureRules[0];
      const needToAdd = Number(nextRule.level) - itemsTotal;
      const bestValue = Math.min(...sorted.map((r) => Number(r.value)));

      if (
        Number(nextRule.value) === bestValue &&
        Number(nextRule.value) === 0
      ) {
        return `Добавьте на ${needToAdd} ₽ до бесплатной доставки`;
      }
      return `Добавьте на ${needToAdd} ₽ до доставки ${nextRule.value} ₽`;
    },
    [clusterDeliveryRules],
  );

  const calculateMaxWriteOff = useCallback(
    (
      orderTotal: number,
      userBonuses: number,
      writeOffPercent: number,
    ): number => {
      if (!orderTotal || !writeOffPercent) return 0;
      const maxByPercent = (orderTotal * writeOffPercent) / 100;
      const maxByBonuses = userBonuses;
      const maxByOrder = orderTotal;
      return Math.floor(Math.min(maxByPercent, maxByBonuses, maxByOrder));
    },
    [],
  );

  const calculateEarnedBonuses = (
    itemsTotal: number,
    bonusPercent: number,
    bonusesToSpend: number = 0,
  ): number => {
    if (!itemsTotal || !bonusPercent) return 0;
    const amountAfterBonus = Math.max(0, itemsTotal - bonusesToSpend);
    const raw = (amountAfterBonus * bonusPercent) / 100;
    return Math.floor(raw);
  };

  const getNextBonusLevelText = useCallback(
    (userTotalSpent: number, bonusRules: Rule[]): string | null => {
      if (!bonusRules?.length) return null;

      const sortedRules = [...bonusRules].sort(
        (a, b) => Number(a.level) - Number(b.level),
      );

      let nextLevel: Rule | null = null;
      for (let i = 0; i < sortedRules.length; i++) {
        if (Number(sortedRules[i].level) > userTotalSpent) {
          nextLevel = sortedRules[i];
          break;
        }
      }

      if (!nextLevel) return null;

      const needToSpend = Number(nextLevel.level) - userTotalSpent;
      return `Еще ${needToSpend} ₽ и будете получать ${nextLevel.value}% бонусами`;
    },
    [],
  );

  return {
    applicablePromos: [],
    clusterDeliveryRules,
    calculateDiscount,
    findApplicablePromos,
    isPromoApplicable,
    roundMoney,
    calculateItemsTotal,
    getBestDeliveryRuleText,
    calculateMaxWriteOff,
    calculateEarnedBonuses,
    getNextBonusLevelText,
  };
};
